import { MainLayout } from 'components/Layout';
import React, { useEffect } from 'react';

export const TermsOfService = () => {
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  return (
    <MainLayout title="Terms of Service" description="Terms of Service for Justknow">
      <div className="px-4 py-6 max-w-screen-md mx-auto">

        <h1 className="text-3xl font-semibold mb-4">Terms of Service</h1>
        <p className="text-sm text-gray-600 mb-6">Last updated: 27th April, 2026</p>

        <p className="mb-4">
          These Terms of Service (“Terms”) constitute a legally binding agreement between you and Justknow
          governing your access to and use of the Justknow application, platform, and services.
          By downloading, installing, accessing, or using Justknow, you acknowledge that you have read,
          understood, and agreed to be bound by these Terms. If you do not agree, you must not use the platform.
        </p>

        {/* Nature */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">1. Nature of the Platform</h2>
        <p className="mb-4">
          Justknow is a location-based technology platform that enables users to communicate, share content,
          discover nearby individuals and businesses, and coordinate peer-to-peer services such as transportation
          and delivery. Justknow does not provide transportation, delivery, or commercial goods directly.
          All interactions, agreements, and transactions occur between independent users.
        </p>

        {/* License */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">2. License Grant (EULA)</h2>
        <p className="mb-4">
          Subject to your compliance with these Terms, Justknow grants you a limited, non-exclusive,
          non-transferable, non-sublicensable, and revocable license to download, install, and use
          the application solely for personal, non-commercial purposes.
        </p>
        <p className="mb-4">
          This license does not grant you any ownership rights in the application. All rights not expressly
          granted are reserved by Justknow.
        </p>

        {/* Ownership */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">3. Ownership and Intellectual Property</h2>
        <p className="mb-4">
          The Justknow platform, including its design, software, algorithms, trademarks, and all associated
          intellectual property rights, remains the exclusive property of Justknow. You are granted only a
          limited right to use the platform under these Terms and acquire no ownership interest.
        </p>

        {/* Restrictions */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">4. Restrictions on Use</h2>
        <p className="mb-4">
          You may not copy, modify, distribute, sell, lease, reverse engineer, decompile, or otherwise attempt
          to extract the source code of the application. You may not exploit the platform for unauthorized
          commercial purposes or interfere with its operation, security, or integrity.
        </p>

        {/* Eligibility */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">5. Eligibility and Account Integrity</h2>
        <p className="mb-4">
          You must be at least 18 years old to use Justknow. You agree to provide accurate and identifiable
          information and to maintain the security of your account. You are fully responsible for all activities
          conducted under your account.
        </p>

        {/* Messaging */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">6. Messaging and Communication</h2>
        <p className="mb-4">
          Justknow provides end-to-end encrypted messaging. Communication between users occurs only after
          a connection request is accepted. Users are shown identifiable information prior to connection,
          and anonymous interactions are not supported.
        </p>
        <p className="mb-4">
          Once established, conversations may continue regardless of location changes. While encryption
          is used, Justknow cannot guarantee absolute security of communications.
        </p>

        {/* Posts */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">7. User Content and Posts</h2>
        <p className="mb-4">
          Users may create and share content within geographically defined areas. Content visibility depends
          on user location. You retain ownership of your content but grant Justknow a worldwide, royalty-free
          license to host, display, distribute, and process such content for platform functionality.
        </p>
        <p className="mb-4">
          You are solely responsible for your content and agree not to post unlawful, harmful, or abusive material.
        </p>

        {/* Ride */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">8. Ride Coordination</h2>
        <p className="mb-4">
          Justknow facilitates ride requests between users. Pricing is user-defined and may be negotiated.
          Justknow does not provide transportation services and is not responsible for the conduct, safety,
          or performance of drivers or passengers.
        </p>

        {/* Business */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">9. Business and Delivery Services</h2>
        <p className="mb-4">
          The platform enables interactions between users and businesses. Orders are fulfilled by independent
          parties. Justknow does not guarantee product quality, delivery outcomes, or service reliability.
        </p>

        {/* Safety */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">10. Safety, Moderation, and Enforcement</h2>
        <p className="mb-4">
          Justknow enforces a strict zero-tolerance policy for objectionable content and abusive behavior.
          Users are provided with tools to report, block, and filter content. All reports are reviewed within
          24 hours, and appropriate action is taken, including removal of content and termination of accounts.
        </p>

        {/* Takedown */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">11. Content Takedown</h2>
        <p className="mb-4">
          Justknow reserves the right to remove or restrict access to any content that violates these Terms
          or applicable laws. Repeat offenders may be permanently banned without notice.
        </p>

        {/* Third Party */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">12. Third-Party Interactions</h2>
        <p className="mb-4">
          Justknow acts solely as an intermediary platform. It is not responsible for user behavior,
          agreements, disputes, or transactions between users.
        </p>

        {/* Availability */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">13. Service Availability</h2>
        <p className="mb-4">
          The platform is provided on an “as available” basis. Justknow does not guarantee uninterrupted
          or error-free operation and may modify or discontinue features at any time.
        </p>

        {/* Apple */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">14. App Store Terms (Apple EULA Clause)</h2>
        <p className="mb-4">
          These Terms are between you and Justknow, not Apple Inc. Apple has no responsibility for the
          application or its content and provides no maintenance or support services.
        </p>
        <p className="mb-4">
          In the event of any failure of the application to conform to any warranty, you may notify Apple,
          and Apple may refund the purchase price, if any. Apple shall have no further obligation.
        </p>

        {/* Export */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">15. Export Compliance</h2>
        <p className="mb-4">
          You agree not to use or export the application in violation of applicable export laws and regulations.
        </p>

        {/* Termination */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">16. Termination</h2>
        <p className="mb-4">
          Justknow reserves the right to suspend or terminate your access at any time for violation of these Terms
          or for conduct that poses a risk to users or the platform.
        </p>

        {/* Liability */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">17. Limitation of Liability</h2>
        <p className="mb-4">
          To the fullest extent permitted by law, Justknow shall not be liable for any indirect, incidental,
          consequential, or punitive damages arising from your use of the platform, including interactions
          with other users or third parties.
        </p>

        {/* Law */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">18. Governing Law</h2>
        <p className="mb-4">
          These Terms are governed by the laws of Uganda.
        </p>

        {/* Changes */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">19. Changes to Terms</h2>
        <p className="mb-4">
          We may update these Terms at any time. Continued use of the platform constitutes acceptance
          of the revised Terms.
        </p>

        {/* Contact */}
        <h2 className="text-2xl font-semibold mt-6 mb-2">Contact</h2>
        <p className="mb-4">
          jonahgeek@gmail.com
        </p>

      </div>
    </MainLayout>
  );
};
